import React, { useCallback, useEffect, useState } from 'react';
import { Plus, Trash2, Plane, Loader2 } from 'lucide-react';
import { format } from 'date-fns';
import { outOfOfficeApi } from '../../api/services';
import type { OutOfOffice } from '../../types/models';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';

// ---------------------------------------------------------------------------
// OutOfOfficeCard — small Preferences card listing upcoming OOO windows with
// add / remove controls. Two flags per window: suppress the daily briefing
// email (default true) and block work allocation by Start My Day (default true).
// Phase 3 task 3.11.
// ---------------------------------------------------------------------------

const REASONS = ['Holiday', 'Travel', 'Training', 'Sick', 'Other'] as const;

function formatWindow(entry: OutOfOffice): string {
  const start = new Date(entry.startDate);
  const end = new Date(entry.endDate);
  if (entry.startDate === entry.endDate) {
    return format(start, 'EEE d MMM yyyy');
  }
  return `${format(start, 'd MMM')} to ${format(end, 'd MMM yyyy')}`;
}

export default function OutOfOfficeCard() {
  const [entries, setEntries] = useState<OutOfOffice[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    startDate: '',
    endDate: '',
    reason: 'Holiday',
    notes: '',
    suppressBriefing: true,
    blockWorkAllocation: true,
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const today = format(new Date(), 'yyyy-MM-dd');
      const futureCutoff = format(
        new Date(Date.now() + 180 * 24 * 60 * 60 * 1000),
        'yyyy-MM-dd',
      );
      const data = await outOfOfficeApi.getActive(today, futureCutoff);
      setEntries(data);
    } catch (e: any) {
      setError(e?.response?.data?.error ?? 'Could not load out-of-office.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const resetForm = () =>
    setForm({
      startDate: '',
      endDate: '',
      reason: 'Holiday',
      notes: '',
      suppressBriefing: true,
      blockWorkAllocation: true,
    });

  const handleAdd = async () => {
    if (!form.startDate || !form.endDate) return;
    setSaving(true);
    setError(null);
    try {
      await outOfOfficeApi.create(form);
      setOpen(false);
      resetForm();
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.error ?? 'Could not save out-of-office.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    setError(null);
    try {
      await outOfOfficeApi.delete(id);
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.error ?? 'Could not delete out-of-office.');
    }
  };

  return (
    <Card>
      <CardContent className="pt-6">
        <div className="mb-2 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Plane className="h-5 w-5 text-primary" />
            <h3 className="text-base font-semibold">Out of office</h3>
          </div>
          <Button
            size="sm"
            variant="outline"
            onClick={() => { resetForm(); setOpen(true); }}
            className="gap-1.5"
          >
            <Plus className="h-4 w-4" />
            Add window
          </Button>
        </div>

        <p className="mb-3 text-sm text-muted-foreground">
          Mark days as out of office to keep Start My Day from allocating work, and to stop the daily briefing email landing in your inbox.
        </p>

        {error && (
          <Alert variant="error" className="mb-3">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {loading ? (
          <div className="py-3 text-center">
            <Loader2 className="mx-auto h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        ) : entries.length === 0 ? (
          <p className="text-sm italic text-muted-foreground">No out-of-office windows scheduled.</p>
        ) : (
          <div className="flex flex-col gap-2">
            {entries.map((entry) => (
              <div
                key={entry.id}
                className="flex items-center justify-between gap-2 rounded-lg border border-border px-3 py-2"
              >
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-sm font-semibold">{formatWindow(entry)}</span>
                    <Badge variant="secondary">{entry.reason}</Badge>
                    {!entry.blockWorkAllocation && <Badge variant="outline">Working day</Badge>}
                    {!entry.suppressBriefing && <Badge variant="outline">Digest still sent</Badge>}
                  </div>
                  {entry.notes && (
                    <p className="mt-1 block text-xs text-muted-foreground">{entry.notes}</p>
                  )}
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8"
                  aria-label="Delete out-of-office window"
                  onClick={() => handleDelete(entry.id)}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            ))}
          </div>
        )}
      </CardContent>

      <Dialog open={open} onOpenChange={(o) => { if (!o && !saving) setOpen(false); }}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Add out of office</DialogTitle>
          </DialogHeader>
          <div className="flex flex-col gap-3">
            <div className="flex gap-2">
              <div className="flex flex-1 flex-col gap-1.5">
                <Label htmlFor="ooo-start">Start</Label>
                <Input
                  id="ooo-start"
                  type="date"
                  value={form.startDate}
                  onChange={(e) => setForm((f) => ({ ...f, startDate: e.target.value }))}
                />
              </div>
              <div className="flex flex-1 flex-col gap-1.5">
                <Label htmlFor="ooo-end">End</Label>
                <Input
                  id="ooo-end"
                  type="date"
                  value={form.endDate}
                  onChange={(e) => setForm((f) => ({ ...f, endDate: e.target.value }))}
                />
              </div>
            </div>
            <div className="flex flex-col gap-1.5">
              <Label>Reason</Label>
              <Select value={form.reason} onValueChange={(v) => setForm((f) => ({ ...f, reason: v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {REASONS.map((r) => (
                    <SelectItem key={r} value={r}>{r}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="ooo-notes">Notes</Label>
              <Textarea
                id="ooo-notes"
                rows={2}
                placeholder="Optional context, e.g. 'annual leave — phone on airplane mode'"
                value={form.notes}
                onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
              />
            </div>
            <label className="flex items-center gap-2 cursor-pointer">
              <Checkbox
                checked={form.blockWorkAllocation}
                onCheckedChange={(v) => setForm((f) => ({ ...f, blockWorkAllocation: v === true }))}
              />
              <span className="text-sm">Block Start My Day from allocating work</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <Checkbox
                checked={form.suppressBriefing}
                onCheckedChange={(v) => setForm((f) => ({ ...f, suppressBriefing: v === true }))}
              />
              <span className="text-sm">Suppress the daily briefing email</span>
            </label>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)} disabled={saving}>Cancel</Button>
            <Button onClick={handleAdd} disabled={saving || !form.startDate || !form.endDate}>
              {saving ? 'Saving…' : 'Add'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
